/**
 * Staff portal — My Duties
 *
 * Mirrors [admin/employees/[id]/components/EmployeeDutiesTab.tsx]:
 *  - Table / Item / Grid view modes via viewModeStore (`hr.my.duties`)
 *  - Click a row → DutyDetailModal
 *  - Read-only: HR controls assignments
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Card,
  Paper,
  ThemeIcon,
  Button,
  Tooltip,
  SegmentedControl,
  SimpleGrid,
  Alert,
  Modal,
  Divider,
} from '@/components/ui';
import ActionIcon from '@/components/ui/ActionIcon';
import { DataTable, type DataTableColumn } from '@/components/advanced';
import { FadeIn } from '@/components/animations';
import { PageHelpButton } from '@/components/help';
import {
  IconRefresh, IconBriefcase, IconBriefcase2, IconLayoutGrid, IconLayoutList, IconLayoutRows,
  IconEye, IconCalendar, IconRefresh as IconCadence, IconAlertCircle,
} from '@tabler/icons-react';
import { useStaffPortalDutyService } from '@actschurch/ccms-sdk';
import { useViewMode } from '@/stores/viewModeStore';
import { hrKeys } from '../utils/queryKeys';

type DutiesViewMode = 'list' | 'item' | 'grid';

export default function MyDutiesPage() {
  const dutyService = useStaffPortalDutyService();
  const [viewMode, setViewMode] = useViewMode<DutiesViewMode>('hr.my.duties', 'list');
  const [selectedDuty, setSelectedDuty] = useState<any | null>(null);

  const { data: assignments = [], isLoading, isFetching, error, refetch } = useQuery({
    queryKey: hrKeys.myDuties(),
    queryFn: () => dutyService.getMyDuties(),
  });

  const { data: catalogue = [] } = useQuery({
    queryKey: hrKeys.duties(),
    queryFn: () => dutyService.getDutyCatalogue(),
  });

  const dutyById = useMemo(
    () => new Map<number, any>((catalogue as any[]).map((d) => [d.dutyID, d])),
    [catalogue],
  );

  // Enrich each EmployeeDuty assignment with the matching catalogue Duty info.
  const duties = useMemo(
    () =>
      (assignments as any[])
        .map((a) => {
          const d = dutyById.get(a.dutyID);
          if (!d) return null;
          return { ...d, id: d.dutyID };
        })
        .filter(Boolean),
    [assignments, dutyById],
  );

  const columns: DataTableColumn<any>[] = useMemo(() => [
    {
      key: 'name',
      label: 'Duty',
      sortable: true,
      width: 280,
      render: (_v, d) => (
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size="md" variant="light" color="orange">
            <IconBriefcase size={16} />
          </ThemeIcon>
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={500} style={{ whiteSpace: 'nowrap' }}>{d.name ?? 'Duty'}</Text>
            <Text size="xs" c="dimmed">#{d.dutyID}</Text>
          </Group>
        </Group>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      render: (v) => <Text size="sm" c={v ? undefined : 'dimmed'} lineClamp={1}>{(v as string | null) ?? '—'}</Text>,
    },
    {
      key: 'frequency',
      label: 'Frequency',
      width: 130,
      render: (v) => <Text size="sm" c="dimmed">{(v as string | null) ?? '—'}</Text>,
    },
    {
      key: 'priority',
      label: 'Priority',
      width: 110,
      render: (v) => v ? <Badge variant="light" size="sm">{v as string}</Badge> : <Text size="xs" c="dimmed">—</Text>,
    },
    {
      key: 'actions',
      label: '',
      width: 80,
      render: (_v, d) => (
        <Group gap={4} justify="flex-end" wrap="nowrap">
          <ActionIcon variant="subtle" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); setSelectedDuty(d); }}>
            <IconEye size={16} />
          </ActionIcon>
        </Group>
      ),
    },
  ], []);

  const renderItem = () => (
    <Stack gap="sm">
      {duties.map((d: any) => (
        <Paper key={d.dutyID} p="md" withBorder style={{ cursor: 'pointer' }} onClick={() => setSelectedDuty(d)}>
          <Group wrap="nowrap" align="center" gap="lg">
            <Group gap="sm" wrap="nowrap" style={{ width: 280, flexShrink: 0 }}>
              <ThemeIcon size="xl" radius="md" variant="light" color="orange">
                <IconBriefcase size={22} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text size="sm" fw={600}>{d.name ?? 'Duty'}</Text>
                <Text size="xs" c="dimmed">#{d.dutyID}</Text>
              </Stack>
            </Group>
            <Stack gap={2} style={{ width: 180, flexShrink: 0 }}>
              {d.frequency && (
                <Group gap={4} wrap="nowrap">
                  <IconCadence size={11} color="var(--mantine-color-dimmed)" />
                  <Text size="xs" c="dimmed">{d.frequency}</Text>
                </Group>
              )}
              {d.priority && <Badge variant="light" size="xs">{d.priority}</Badge>}
            </Stack>
            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>Description</Text>
              <Text size="sm" lineClamp={1} c={d.description ? undefined : 'dimmed'}>{d.description ?? '—'}</Text>
            </Stack>
            <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
              <ActionIcon variant="subtle" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); setSelectedDuty(d); }}>
                <IconEye size={16} />
              </ActionIcon>
            </Group>
          </Group>
        </Paper>
      ))}
    </Stack>
  );

  const renderGrid = () => (
    <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 3 }}>
      {duties.map((d: any) => (
        <Card key={d.dutyID} withBorder padding="md" radius="md" style={{ cursor: 'pointer' }} onClick={() => setSelectedDuty(d)}>
          <Stack gap="xs">
            <Group justify="space-between" wrap="nowrap">
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon size="md" radius="md" variant="light" color="orange">
                  <IconBriefcase size={16} />
                </ThemeIcon>
                <Stack gap={0}>
                  <Text size="sm" fw={600} lineClamp={1}>{d.name ?? 'Duty'}</Text>
                  <Text size="xs" c="dimmed">#{d.dutyID}</Text>
                </Stack>
              </Group>
              {d.priority && <Badge variant="light" size="xs">{d.priority}</Badge>}
            </Group>
            <Divider />
            {d.frequency && (
              <Group gap={6} wrap="nowrap">
                <IconCadence size={12} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed" truncate>{d.frequency}</Text>
              </Group>
            )}
            <Text size="xs" c={d.description ? 'dimmed' : 'dimmed'} lineClamp={1} fs={d.description ? undefined : 'italic'}>
              {d.description ?? 'No description'}
            </Text>
            {(d.isSundayDuty || d.isEventsDuty) && (
              <Group gap={4}>
                {d.isSundayDuty && <Badge size="xs" variant="light" color="grape" leftSection={<IconCalendar size={10} />}>Sunday</Badge>}
                {d.isEventsDuty && <Badge size="xs" variant="light" color="cyan" leftSection={<IconCalendar size={10} />}>Events</Badge>}
              </Group>
            )}
          </Stack>
        </Card>
      ))}
    </SimpleGrid>
  );

  const loading = isLoading || isFetching;

  return (
    <FadeIn>
      <Stack gap="lg">
        <Group justify="space-between" wrap="wrap">
          <Stack gap={0}>
            <Title order={2}>My Duties</Title>
            <Text size="sm" c="dimmed">Roles and responsibilities assigned to you by HR.</Text>
          </Stack>
          <Group gap="sm" data-help-id="hr.my-duties.actions">
            <Tooltip label="Refresh">
              <ActionIcon variant="subtle" onClick={() => refetch()} loading={loading}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <PageHelpButton pageKey="hr.my-duties" />
          </Group>
        </Group>

        <Paper withBorder p="md" radius="md" data-help-id="hr.my-duties.toolbar">
          <Group justify="space-between" wrap="wrap">
            <Text size="sm" c="dimmed">
              {loading && duties.length === 0 ? 'Loading…' : `${duties.length} dut${duties.length === 1 ? 'y' : 'ies'}`}
            </Text>
            <SegmentedControl
              data-help-id="hr.my-duties.view-switch"
              value={viewMode}
              onChange={(v) => setViewMode(v as DutiesViewMode)}
              size="xs"
              data={[
                { value: 'list', label: <Tooltip label="Table"><Group gap={0} px={4}><IconLayoutList size={14} /></Group></Tooltip> },
                { value: 'item', label: <Tooltip label="Item"><Group gap={0} px={4}><IconLayoutRows size={14} /></Group></Tooltip> },
                { value: 'grid', label: <Tooltip label="Grid"><Group gap={0} px={4}><IconLayoutGrid size={14} /></Group></Tooltip> },
              ]}
            />
          </Group>
        </Paper>

        {error && <Alert color="red" icon={<IconAlertCircle size={16} />}>Failed to load your duties.</Alert>}

        <div data-help-id="hr.my-duties.list">
        {duties.length === 0 && !loading ? (
          <Card withBorder>
            <Stack align="center" py="xl" gap="xs">
              <IconBriefcase2 size={32} color="var(--mantine-color-dimmed)" />
              <Text size="sm" c="dimmed">You have no duties assigned.</Text>
            </Stack>
          </Card>
        ) : viewMode === 'list' ? (
          <Card withBorder p={0}>
            <DataTable<any>
              data={duties}
              columns={columns}
              loading={loading}
              emptyMessage="You have no duties assigned."
              striped
              highlightOnHover
              enableInternalSort
              onRowClick={(row) => setSelectedDuty(row)}
            />
          </Card>
        ) : viewMode === 'item' ? renderItem() : renderGrid()}
        </div>
      </Stack>

      <Modal
        opened={!!selectedDuty}
        onClose={() => setSelectedDuty(null)}
        title={
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon size="lg" radius="md" variant="light" color="orange">
              <IconBriefcase size={18} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text fw={600}>{selectedDuty?.name ?? 'Duty'}</Text>
              <Text size="xs" c="dimmed">#{selectedDuty?.dutyID}</Text>
            </Stack>
          </Group>
        }
        size="md"
        centered
      >
        {selectedDuty && (
          <Stack gap="md">
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>Description</Text>
              <Text size="sm" c={selectedDuty.description ? undefined : 'dimmed'}>{selectedDuty.description ?? 'No description recorded.'}</Text>
            </Stack>
            <Group grow>
              <Stack gap={2}>
                <Text size="xs" c="dimmed">Frequency</Text>
                <Text size="sm">{selectedDuty.frequency ?? '—'}</Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed">Priority</Text>
                <Text size="sm">{selectedDuty.priority ?? '—'}</Text>
              </Stack>
            </Group>
            {(selectedDuty.isSundayDuty || selectedDuty.isEventsDuty) && (
              <Group gap={6}>
                {selectedDuty.isSundayDuty && <Badge size="sm" variant="light" color="grape" leftSection={<IconCalendar size={11} />}>Sunday</Badge>}
                {selectedDuty.isEventsDuty && <Badge size="sm" variant="light" color="cyan" leftSection={<IconCalendar size={11} />}>Events</Badge>}
              </Group>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => setSelectedDuty(null)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </FadeIn>
  );
}
